import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';

const SizeGuide = () => {
  const sizeChart = [
    { us: '7', uk: '6', eu: '40', cm: '25' },
    { us: '7.5', uk: '6.5', eu: '40.5', cm: '25.5' },
    { us: '8', uk: '7', eu: '41', cm: '26' },
    { us: '8.5', uk: '7.5', eu: '42', cm: '26.5' },
    { us: '9', uk: '8', eu: '42.5', cm: '27' },
    { us: '9.5', uk: '8.5', eu: '43', cm: '27.5' },
    { us: '10', uk: '9', eu: '44', cm: '28' },
    { us: '10.5', uk: '9.5', eu: '44.5', cm: '28.5' },
    { us: '11', uk: '10', eu: '45', cm: '29' },
    { us: '11.5', uk: '10.5', eu: '45.5', cm: '29.5' },
    { us: '12', uk: '11', eu: '46', cm: '30' },
    { us: '13', uk: '12', eu: '47.5', cm: '31' },
  ];

  return (
    <div className="min-h-screen bg-concrete-white" data-testid="size-guide-page">
      {/* Header */}
      <div className="bg-deep-obsidian py-16">
        <div className="max-w-7xl mx-auto px-6">
          <Link to="/products" className="inline-flex items-center text-cement-grey hover:text-white transition-colors mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            <span className="uppercase text-xs tracking-widest font-bold">Back to Shop</span>
          </Link>
          <p className="font-bebas text-chicago-red text-lg tracking-widest">HELP</p>
          <h1 className="font-anton text-4xl md:text-5xl text-white tracking-tighter uppercase">SIZE GUIDE</h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Introduction */}
        <div className="bg-white border border-gray-200 p-8 mb-8">
          <h2 className="font-bebas text-2xl tracking-wider mb-4">VINTAGE SIZING NOTES</h2>
          <p className="text-cement-grey leading-relaxed mb-4">
            Vintage basketball shoes often fit differently than modern sneakers. Here are some important considerations:
          </p>
          <ul className="space-y-2 text-cement-grey">
            <li className="flex items-start gap-2">
              <span className="text-chicago-red font-bold">•</span>
              <span><strong>90s Nike/Jordan:</strong> Generally run true to size or slightly narrow. Consider going half size up if you have wide feet.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-chicago-red font-bold">•</span>
              <span><strong>Reebok Question/Answer:</strong> Known for a slightly roomier fit. True to size for most.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-chicago-red font-bold">•</span>
              <span><strong>AND1:</strong> Generally true to size with a performance fit.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-chicago-red font-bold">•</span>
              <span><strong>Converse:</strong> Notoriously run large. Consider going down a full size.</span>
            </li>
          </ul>
        </div>

        {/* Size Chart */}
        <div className="bg-white border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h2 className="font-bebas text-2xl tracking-wider">SIZE CONVERSION CHART</h2>
            <p className="text-cement-grey text-sm mt-1">Men's Basketball Shoe Sizes</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-deep-obsidian text-white">
                <tr>
                  <th className="px-6 py-4 text-left font-bebas tracking-wider">US</th>
                  <th className="px-6 py-4 text-left font-bebas tracking-wider">UK</th>
                  <th className="px-6 py-4 text-left font-bebas tracking-wider">EU</th>
                  <th className="px-6 py-4 text-left font-bebas tracking-wider">CM</th>
                </tr>
              </thead>
              <tbody>
                {sizeChart.map((size, idx) => (
                  <tr key={size.us} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="px-6 py-4 font-bold">{size.us}</td>
                    <td className="px-6 py-4">{size.uk}</td>
                    <td className="px-6 py-4">{size.eu}</td>
                    <td className="px-6 py-4">{size.cm}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Measuring Guide */}
        <div className="bg-white border border-gray-200 p-8 mt-8">
          <h2 className="font-bebas text-2xl tracking-wider mb-4">HOW TO MEASURE YOUR FOOT</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <ol className="space-y-4 text-cement-grey">
                <li className="flex gap-4">
                  <span className="flex-shrink-0 w-8 h-8 bg-chicago-red text-white flex items-center justify-center font-bebas">1</span>
                  <span>Place a piece of paper against a wall on a hard floor.</span>
                </li>
                <li className="flex gap-4">
                  <span className="flex-shrink-0 w-8 h-8 bg-chicago-red text-white flex items-center justify-center font-bebas">2</span>
                  <span>Stand on the paper with your heel against the wall.</span>
                </li>
                <li className="flex gap-4">
                  <span className="flex-shrink-0 w-8 h-8 bg-chicago-red text-white flex items-center justify-center font-bebas">3</span>
                  <span>Mark the longest part of your foot (usually big toe).</span>
                </li>
                <li className="flex gap-4">
                  <span className="flex-shrink-0 w-8 h-8 bg-chicago-red text-white flex items-center justify-center font-bebas">4</span>
                  <span>Measure from the wall to the mark in centimeters.</span>
                </li>
                <li className="flex gap-4">
                  <span className="flex-shrink-0 w-8 h-8 bg-chicago-red text-white flex items-center justify-center font-bebas">5</span>
                  <span>Use the chart above to find your size. If between sizes, go up.</span>
                </li>
              </ol>
            </div>
            <div className="flex items-center justify-center">
              <div className="bg-gray-100 p-8 text-center">
                <p className="font-bebas text-lg tracking-wider mb-2">NEED HELP?</p>
                <p className="text-cement-grey text-sm mb-4">
                  Contact us if you're unsure about sizing for a specific vintage shoe.
                </p>
                <Button variant="outline" className="border-2 border-deep-obsidian uppercase tracking-widest text-xs font-bold">
                  Contact Support
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <Link to="/products">
            <Button className="bg-chicago-red hover:bg-chicago-red/90 text-white uppercase tracking-widest text-sm font-bold py-4 px-8 rounded-sm">
              Shop Collection
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SizeGuide;
